// @ts-strict-ignore
import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from '@env/environment';
import { State, selectDataSelector } from './store';

import { LoadData, SortData } from './store/data/actions';
import { BehaviorSubject } from 'rxjs';
import { ItemType } from '../run-management/definitions';
import { ColumnTotals } from '../../services/run.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'filterable-table',
    templateUrl: './filterable-table.component.html',
    styleUrls: ['./filterable-table.component.scss'],
    standalone: false
})
export class FilterableTableComponent {
  private NUM_OF_STATIC_COLS = 1;
  @Input()
  set data(value: any) {
    this.store.dispatch(new LoadData(value));
  }
  @Input()
  set columnsToRender(columnsToRender: any) {
    this._columnsToRender = columnsToRender;
    this.columnWidth = 100 / (columnsToRender.length + this.NUM_OF_STATIC_COLS);
  }
  @Input() columnChildrenToRender: any;
  @Input() columnTotals: ColumnTotals = null;
  @Input() selectedColTotals: ColumnTotals;
  @Input() actions: object = null;
  @Input() withRowCheckBoxes: boolean;
  @Input() runsToUncheck = [];
  @Input() loading = false;
  @Input() selectedTotal: number;
  @Input() headersHaveItems = true;
  @Input() enableSelectAllPages = true;
  @Output() rowCheckedEmitter = new EventEmitter<{ header: any; item: any }>();
  @Output() selectAllEmitter = new EventEmitter();
  @Output() selectAllOnPageEmitter = new EventEmitter();

  public columnWidth: number;
  // tslint:disable: variable-name
  public _columnsToRender: any;
  public sort = '';
  public sortDir = 'ASC';
  public showSelectAllPopup = false;
  public isTrustee = environment.tportal;

  public data$ = new BehaviorSubject<any>([]);
  public loadingCheck: boolean;
  constructor(private store: Store<State>) {
    this.store
      .select(selectDataSelector)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (dataStore) => {
          if (dataStore.data) {
            // this needs to be done as dataStore is read-only
            this.data$.next(
              dataStore.data?.map((header) => ({
                ...header,
                showItems: false,
                ...(header.items ? { items: header.items.map((item) => ({ ...item })) } : {}),
              })),
            );
          } else {
            this.data$.next([]);
          }
        },
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loading) {
      this.loadingCheck = changes.loading.currentValue;
    }
  }

  handleSort(key: string) {
    this.sortDir = this.sortDir === 'ASC' ? 'DESC' : 'ASC';
    this.sort = key;

    const column = this._columnsToRender.find((col: { key: string }) => col.key === key);
    if (column.sortCallBack !== undefined) {
      // this hook needs to call the service which handles sorting,
      // and that service needs to update the data which is being passed back into the component
      column.sortCallBack(column.title, this.sortDir);
    } else {
      this.store.dispatch(new SortData({ key, direction: this.sortDir }));
    }
  }

  rowChecked($event: { header: any; item: any; bulkMode: boolean }) {
    this.rowCheckedEmitter.emit($event);
  }

  checkAll($event) {
    let data = this.data$.getValue();
    const tableHeaderChecked = $event.target.checked;

    if (this.enableSelectAllPages) {
      this.showSelectAllPopup = tableHeaderChecked;
    }

    data.forEach((header) => {
      if (this.headersHaveItems) {
        header.items.forEach((item: ItemType) => {
          if (item.checked !== tableHeaderChecked && item.isMatch) {
            item.checked = !item.checked;
            this.rowChecked({ header, item, bulkMode: true });
          }
        });
      } else {
        if (header.checked !== tableHeaderChecked) {
          header.checked = !header.checked;
          this.rowChecked({ header, item: null, bulkMode: true });
        }
      }
    });
    this.data$.next(data);
    this.selectAllOnPageEmitter.emit(tableHeaderChecked);
  }

  allChecked() {
    return this.data$.getValue().every((header: any) => {
      if (this.headersHaveItems) {
        return header.items.every((item) => {
          return !(item.isMatch && !item.checked);
        });
      } else {
        return header.checked;
      }
    });
  }

  selectOnAllPages() {
    this.selectAllEmitter.emit();
    this.showSelectAllPopup = false;
  }

  getColumnTotals(key: string) {
    return this.selectedTotal ? this.selectedColTotals[key] : this.columnTotals[key] || 0;
  }
}
